// Home.js
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { EventContext } from '../context/EventContext';
import { getAllNews } from '../api/news';
import { getRewards } from '../api/rewards';
import { getGalleryImages } from '../api/gallery';
import EventCard from '../components/EventCard';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ResearchDayModal from '../components/ResearchDayModal';
import kosTeamImage from '../assets/kos_team_2025_2027.png';
import kosIntroVideo from '../assets/KOS_intro_video.webm';
import './Home.css';

const Home = () => {
  const { user, userData, loading: authLoading, isAuthenticated } = useContext(AuthContext);
  const { events, loading: eventsLoading, fetchEvents } = useContext(EventContext);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [showConferenceNotice, setShowConferenceNotice] = useState(true);
  const [showResearchDayModal, setShowResearchDayModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 6;

  useEffect(() => {
    fetchEvents();
    fetchNews();
    fetchRewards();
    fetchGallery();

    // Automatically trigger announcement modal for visitors on arrival
    const hasSeenAnnouncement = sessionStorage.getItem('kos_research_day_2026_seen');
    if (!hasSeenAnnouncement) {
      const timer = setTimeout(() => {
        setShowResearchDayModal(true);
        sessionStorage.setItem('kos_research_day_2026_seen', 'true');
      }, 700);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNews = async () => {
    try {
      setNewsLoading(true);
      const data = await getAllNews();
      setNews(data.slice(0, 3)); // Get latest 3 news items
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchRewards = async () => {
    try {
      setRewardsLoading(true);
      const data = await getRewards();
      setRewards(data.filter(r => r.active).slice(0, 3)); // Get latest 3 active rewards
    } catch (err) {
      console.error('Error fetching rewards:', err);
    } finally {
      setRewardsLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      setGalleryLoading(true);
      const data = await getGalleryImages();
      setGallery(data);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setGalleryLoading(false);
    }
  };

  const upcomingEvents = events
    .filter(event => new Date(event.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 3);

  const getEventsForDate = (day) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day &&
        eventDate.getMonth() === currentMonth &&
        eventDate.getFullYear() === currentYear;
    });
  };

  const handleDateClick = (day) => {
    const eventsOnDay = getEventsForDate(day);
    if (eventsOnDay.length > 0) {
      setSelectedDate(day);
      setShowEventModal(true);
    }
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedDate(null);
  };

  // Gallery functions
  const openImageModal = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleNextImage = () => {
    const currentIndex = gallery.findIndex(img => img.id === selectedImage.id);
    if (currentIndex < gallery.length - 1) {
      setSelectedImage(gallery[currentIndex + 1]);
    }
  };

  const handlePreviousImage = () => {
    const currentIndex = gallery.findIndex(img => img.id === selectedImage.id);
    if (currentIndex > 0) {
      setSelectedImage(gallery[currentIndex - 1]);
    }
  };

  // Pagination calculations
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = gallery.slice(indexOfFirstImage, indexOfLastImage);
  const totalPages = Math.ceil(gallery.length / imagesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to gallery section
    document.querySelector('.gallery-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (authLoading) {
    return (
      <div className="page home-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Kuwait Ophthalmology Program Research Day Announcement Bar */}
      <div className="research-day-announcement-bar">
        <div className="research-day-announcement-content">
          <span className="announcement-badge">📢 SPECIAL ANNOUNCEMENT</span>
          <span className="announcement-text">
            <strong>Kuwait Ophthalmology Program Research Day</strong> — October 29th, 2026 at KIMS Theatre
          </span>
          <div className="announcement-bar-actions">
            <button
              className="announcement-view-schedule-btn"
              onClick={() => setShowResearchDayModal(true)}
            >
              📋 View Schedule & Details
            </button>
            <a
              href="/KuwaitOphthalmologyProgramResearch2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="KuwaitOphthalmologyProgramResearch2026.pdf"
              className="announcement-pdf-btn"
              title="Download official PDF schedule"
            >
              📥 PDF
            </a>
          </div>
        </div>
      </div>

      {/* Conference Notice Bar */}
      {showConferenceNotice && (
        <div className="conference-notice-bar">
          <div className="conference-notice-content">
            <span className="notice-icon">📢</span>
            <div className="notice-text">
              <strong>Scientific Conferences</strong>
              <span className="notice-separator">|</span>
              <span>Apply for registration and sponsorship for upcoming scientific conferences</span>
              <span className="notice-separator">|</span>
              <div className="notice-actions">
                <Link to="/conference-registration" className="notice-register-btn">
                  Register Now →
                </Link>
              </div>
            </div>
          </div>
          <button
            className="notice-close-btn"
            onClick={() => setShowConferenceNotice(false)}
            aria-label="Close notice"
          >
            ×
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <video className="hero-video-background" autoPlay loop muted playsInline>
          <source src={kosIntroVideo} type="video/webm" />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <p className="hero-small-title">Welcome in KOS</p>
          <h1 className="hero-title">Founder Members and Mission to Elevate Eye Care Awareness and Services</h1>
          <p className="hero-subtitle">
            Stay informed with our latest updates, including events, posts, and announcements.
          </p>
          {!isAuthenticated ? (
            <div className="hero-actions">
              <Link to="/register">
                <Button variant="primary" size="large">Join KOS Today</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="large">Member Login</Button>
              </Link>
            </div>
          ) : (
            <div className="hero-welcome">
              <h2>Welcome back, {userData?.name || user?.displayName || 'Member'}!</h2>
              <p>You have {userData?.points || 0} points</p>
            </div>
          )}
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-section">
        <div className="container">
          <h2 className="section-title">Welcome to Our Society</h2>
          <div className="about-content-wrapper">
            <div className="about-text-content">
              <h3 className="section-subtitle">Founder Members and Mission to Elevate Eye Care Awareness and Services</h3>
              <p className="section-description">
                The Ophthalmologists Association was established in the year 2000 under the umbrella of the Kuwait Medical Association. The founder council comprised of a President, Vice President, and Treasurer, along with additional members.
              </p>
              <p className="section-description">
                The objectives of the association are multifaceted, focusing on spreading awareness about the most important eye diseases and correcting misinformation circulated in the media. It also contributes to the organization of seminars and practical workshops that serve continuous medical education and enhance the level of medical services and specialization for ophthalmologists in the State of Kuwait.
              </p>
            </div>
            <div className="about-image-content">
              <img src={kosTeamImage} alt="KOS Team 2025-2027" />
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates Section - Three Columns */}
      <section className="latest-updates-section">
        <div className="container">
          <h2 className="section-title">Stay Updated</h2>
          <div className="three-column-grid">
            {/* Latest Events Column */}
            <Card className="column-card">
              <div className="column-header">
                <div className="column-icon">📅</div>
                <h3>Latest Events</h3>
              </div>
              <div className="column-content">
                {eventsLoading ? (
                  <div className="column-loading">Loading...</div>
                ) : upcomingEvents.length > 0 ? (
                  <div className="items-list">
                    {upcomingEvents.map(event => (
                      <div key={event.id} className="list-item">
                        <div className="item-date">
                          {new Date(event.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="item-details">
                          <h4>{event.title}</h4>
                          <p>{event.location}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-message">No upcoming events</p>
                )}
              </div>
              <div className="column-footer">
                <Link to={isAuthenticated ? "/events" : "/login"}>
                  <Button variant="text">View All Events →</Button>
                </Link>
              </div>
            </Card>

            {/* Latest News Column */}
            <Card className="column-card">
              <div className="column-header">
                <div className="column-icon">📰</div>
                <h3>Latest News</h3>
              </div>
              <div className="column-content">
                {newsLoading ? (
                  <div className="column-loading">Loading...</div>
                ) : news.length > 0 ? (
                  <div className="items-list">
                    {news.map(post => (
                      <div key={post.id} className="list-item">
                        <div className="item-date">
                          {new Date(post.createdAt?.toDate ? post.createdAt.toDate() : post.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="item-details">
                          <h4>{post.title}</h4>
                          <p>{post.content?.substring(0, 60)}...</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-message">No news available</p>
                )}
              </div>
              <div className="column-footer">
                <Link to={isAuthenticated ? "/news" : "/login"}>
                  <Button variant="text">View All News →</Button>
                </Link>
              </div>
            </Card>

            {/* Calendar Column */}
            <Card className="column-card">
              <div className="column-header">
                <div className="column-icon">📆</div>
                <h3>Calendar</h3>
              </div>
              <div className="column-content">
                <div className="calendar-mini">
                  <div className="calendar-month">
                    {new Date().toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="calendar-grid">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                      <div key={i} className="calendar-day-header">{day}</div>
                    ))}
                    {Array.from({ length: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getDay() }, (_, i) => (
                      <div key={`empty-${i}`} className="calendar-day empty"></div>
                    ))}
                    {Array.from({ length: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() }, (_, i) => {
                      const day = i + 1;
                      const isToday = day === new Date().getDate();
                      const eventsOnDay = getEventsForDate(day);
                      const hasEvent = eventsOnDay.length > 0;
                      return (
                        <div
                          key={day}
                          className={`calendar-day ${isToday ? 'today' : ''} ${hasEvent ? 'has-event' : ''}`}
                          onClick={() => hasEvent && handleDateClick(day)}
                          style={{ cursor: hasEvent ? 'pointer' : 'default' }}
                          title={hasEvent ? `${eventsOnDay.length} event${eventsOnDay.length > 1 ? 's' : ''}` : ''}
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="column-footer">
                <Link to={isAuthenticated ? "/events" : "/login"}>
                  <Button variant="text">View Full Calendar →</Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {isAuthenticated && (
        <section className="events-preview-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Upcoming Events</h2>
              <Link to="/events">
                <Button variant="text">View All Events →</Button>
              </Link>
            </div>
            {eventsLoading ? (
              <div className="loading">Loading events...</div>
            ) : upcomingEvents.length > 0 ? (
              <div className="events-grid">
                {upcomingEvents.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <Card className="empty-state">
                <p>No upcoming events at the moment. Check back soon!</p>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Rewards Section */}
      {isAuthenticated && (
        <section className="rewards-preview-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">KOS Promotions</h2>
              <Link to="/rewards">
                <Button variant="text">View All Rewards →</Button>
              </Link>
            </div>
            {rewardsLoading ? (
              <div className="loading">Loading rewards...</div>
            ) : rewards.length > 0 ? (
              <div className="rewards-grid">
                {rewards.map(reward => (
                  <Card key={reward.id} className="reward-preview-card">
                    {reward.imageUrl ? (
                      <div className="reward-preview-image">
                        <img src={reward.imageUrl} alt={reward.merchantName} />
                      </div>
                    ) : (
                      <div className="reward-preview-image placeholder">
                        <div className="merchant-initial">
                          {reward.merchantName?.charAt(0).toUpperCase() || 'M'}
                        </div>
                      </div>
                    )}
                    <div className="reward-preview-content">
                      <div className="reward-meta">
                        <span className="merchant-name">{reward.merchantName}</span>
                        <span className="discount-badge">{reward.discount} OFF</span>
                      </div>
                      <h3>{reward.title}</h3>
                      <p className="reward-description">
                        {reward.description ? reward.description.substring(0, 100) + '...' : 'Exclusive member discount'}
                      </p>
                      <div className="reward-footer">
                        <div className="points-badge">
                          <span className="points-icon">⭐</span>
                          <span>Tier {reward.requiredTier}</span>
                        </div>
                        <Link to="/rewards">
                          <Button variant="text" size="small">View Details →</Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="empty-state">
                <p>No rewards available at the moment.</p>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Latest News Section */}
      {isAuthenticated && (
        <section className="news-preview-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Latest News</h2>
              <Link to="/news">
                <Button variant="text">View All News →</Button>
              </Link>
            </div>
            {newsLoading ? (
              <div className="loading">Loading news...</div>
            ) : news.length > 0 ? (
              <div className="news-grid">
                {news.map(post => (
                  <Card key={post.id} className="news-card">
                    {post.imageUrl && (
                      <div className="news-image">
                        <img src={post.imageUrl} alt={post.title} />
                      </div>
                    )}
                    <div className="news-content">
                      <div className="news-meta">
                        <span className="news-category">{post.category || 'General'}</span>
                        <span className="news-date">
                          {new Date(post.createdAt?.toDate ? post.createdAt.toDate() : post.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.content?.substring(0, 150)}...</p>
                      <Link to="/news">
                        <Button variant="text">Read More →</Button>
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="empty-state">
                <p>No news available at the moment.</p>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Call to Action */}
      {!isAuthenticated && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Join Our Community?</h2>
              <p>Become a member today and access exclusive events, resources, and networking opportunities</p>
              <div className="cta-actions">
                <Link to="/register">
                  <Button variant="primary" size="large">Register Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Photo Gallery</h2>
            {isAuthenticated && userData?.role === 'admin' && (
              <Link to="/admin/gallery">
                <Button variant="outline">Manage Gallery</Button>
              </Link>
            )}
          </div>
          {galleryLoading ? (
            <div className="loading">Loading gallery...</div>
          ) : gallery.length > 0 ? (
            <>
              <div className="gallery-grid">
                {currentImages.map((image) => (
                  <div
                    key={image.id}
                    className="gallery-item"
                    onClick={() => openImageModal(image)}
                  >
                    <img src={image.imageUrl} alt={image.caption || 'Gallery image'} />
                    {image.caption && (
                      <div className="gallery-caption">{image.caption}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="pagination-pages">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <Card className="empty-state">
              <p>No images in gallery yet.</p>
            </Card>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Contact Us</h2>
          <div className="contact-info">
            <p className="contact-item">
              <strong>Address:</strong> Kuwait Medical Association, Jabriya, Kuwait
            </p>
            <p className="contact-item">
              <strong>Email:</strong> <a href="mailto:admin@kos.org.kw">admin@kos.org.kw</a>
            </p>
          </div>
        </div>
      </section>

      {/* Event Modal */}
      {showEventModal && selectedDate && (
        <div className="event-modal-overlay" onClick={closeEventModal}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <h3>Events on {new Date().toLocaleDateString('en-US', { month: 'long' })} {selectedDate}</h3>
              <button className="modal-close-btn" onClick={closeEventModal}>×</button>
            </div>
            <div className="event-modal-content">
              {getEventsForDate(selectedDate).map(event => (
                <div key={event.id} className="modal-event-item">
                  <div className="modal-event-time">
                    {new Date(event.date).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div className="modal-event-details">
                    <h4>{event.title}</h4>
                    <p className="modal-event-location">📍 {event.location}</p>
                    {event.description && (
                      <p className="modal-event-description">{event.description}</p>
                    )}
                    <div className="modal-event-points">
                      ⭐ {event.pointsAwarded || 0} points
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="event-modal-footer">
              <Link to="/events" onClick={closeEventModal}>
                <Button variant="primary">View All Events</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Image Expansion Modal */}
      {showImageModal && selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>×</button>

            {/* Navigation arrows */}
            {gallery.findIndex(img => img.id === selectedImage.id) > 0 && (
              <button className="modal-nav-btn modal-prev-btn" onClick={handlePreviousImage}>
                ‹
              </button>
            )}
            {gallery.findIndex(img => img.id === selectedImage.id) < gallery.length - 1 && (
              <button className="modal-nav-btn modal-next-btn" onClick={handleNextImage}>
                ›
              </button>
            )}

            <img
              src={selectedImage.imageUrl}
              alt={selectedImage.caption || 'Gallery image'}
              className="modal-image"
            />
            {selectedImage.caption && (
              <div className="modal-image-caption">{selectedImage.caption}</div>
            )}
          </div>
        </div>
      )}

      {/* Kuwait Ophthalmology Program Research Day Modal */}
      <ResearchDayModal
        isOpen={showResearchDayModal}
        onClose={() => setShowResearchDayModal(false)}
      />
    </div>
  );
};

export default Home;
